import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import type { BiometricOptions } from '@capgo/capacitor-native-biometric';
import { LocalStorageFacade } from '@application/localstorage';
import { APP_CONFIG } from '@application/config';
import { IAuthAccessToken } from '../entities/authentication.interface';
import {
  BIOMETRIC_INFO_DEFAULTS,
  BIOMETRIC_INFO_KEY,
  IBiometricInfo,
} from '../entities/biometric.model';

/**
 * Infra wrapper around the native biometric plugin + device secure storage
 * (Keychain / Keystore), plus the persisted biometric-info object. Stateless —
 * all app state and orchestration live in `BiometricStore`. Native plugin calls
 * are a safe no-op on non-native platforms, so the whole feature disappears on web.
 */
@Injectable({ providedIn: 'root' })
export class BiometricService {
  private readonly localStorage = inject(LocalStorageFacade);
  private readonly appConfig = inject(APP_CONFIG);

  /** Per-app Keychain/Keystore namespace for the stored session. */
  private readonly server = `${this.appConfig.app_id ?? this.appConfig.appSource}.biometric`;

  /**
   * The plugin is loaded on first use, never at import time.
   *
   * It ships as ESM and this service is re-exported by the `@account/data-access`
   * barrel, so a static import put it in the module graph of everything that
   * touches the barrel — including Jest, which cannot parse it and failed whole
   * suites that have nothing to do with biometrics. On the web it also went into
   * the bundle for a feature that only ever runs on a native shell.
   */
  private plugin?: typeof import('@capgo/capacitor-native-biometric');

  private async loadPlugin(): Promise<
    typeof import('@capgo/capacitor-native-biometric')
  > {
    this.plugin ??= await import('@capgo/capacitor-native-biometric');
    return this.plugin;
  }

  public isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** True only when the device has usable biometric hardware enrolled. */
  public async isAvailable(): Promise<boolean> {
    if (!this.isNative()) return false;
    try {
      const { NativeBiometric, BiometryType } = await this.loadPlugin();
      const result = await NativeBiometric.isAvailable({ useFallback: true });
      return result.isAvailable && result.biometryType !== BiometryType.NONE;
    } catch {
      return false;
    }
  }

  /** Prompts the native biometric dialog; rejects if it fails or is cancelled. */
  public async verifyIdentity(options: BiometricOptions): Promise<void> {
    const { NativeBiometric } = await this.loadPlugin();
    return NativeBiometric.verifyIdentity(options);
  }

  /** Writes the session to the Keychain/Keystore (the `password` slot holds the session JSON). */
  public async storeSession(session: IAuthAccessToken): Promise<void> {
    await this.deleteSession();
    const { NativeBiometric } = await this.loadPlugin();
    await NativeBiometric.setCredentials({
      username: session.PersonId ?? 'session',
      password: JSON.stringify(session),
      server: this.server,
    });
  }

  /** Reads and deserializes the session previously written by {@link storeSession}. */
  public async readSession(): Promise<IAuthAccessToken> {
    const { NativeBiometric } = await this.loadPlugin();
    const credentials = await NativeBiometric.getCredentials({ server: this.server });
    return JSON.parse(credentials.password) as IAuthAccessToken;
  }

  /** Removes the stored session from the Keychain/Keystore. */
  public async deleteSession(): Promise<void> {
    if (!this.isNative()) return;
    try {
      const { NativeBiometric } = await this.loadPlugin();
      await NativeBiometric.deleteCredentials({ server: this.server });
    } catch {
      // No stored credential to delete — ignore.
    }
  }

  /** The persisted biometric info, fully defaulted (never undefined). */
  public readInfo(): IBiometricInfo {
    const raw = this.localStorage.getItem(BIOMETRIC_INFO_KEY);
    if (!raw) return { ...BIOMETRIC_INFO_DEFAULTS };
    try {
      return { ...BIOMETRIC_INFO_DEFAULTS, ...JSON.parse(raw) };
    } catch {
      return { ...BIOMETRIC_INFO_DEFAULTS };
    }
  }

  /** Merges a partial update into the stored biometric info object. */
  public patchInfo(partial: Partial<IBiometricInfo>): void {
    this.localStorage.setItem(
      BIOMETRIC_INFO_KEY,
      JSON.stringify({ ...this.readInfo(), ...partial })
    );
  }

  /** Removes the whole biometric info object. */
  public clearInfo(): void {
    this.localStorage.removeItem(BIOMETRIC_INFO_KEY);
  }
}
